import React, { useState } from 'react';
import {
  Box,
  Button,
  TextInput,
  ModalBody,
  ModalFooter,
  Switch,
  Text,
  Chip,
  ChipGroup,
  Alert,
  Heading,
  Badge,
} from '@razorpay/blade/components';

import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { useFormWithProvider } from 'forms';
import { EmployeeLoansCategoryFormData } from '../types';
import { SUGGESTED_CATEGORY_NAMES } from '../constants';
import { CardWrapper } from './CardWrapper';
import LoanCoins from '../../../../assets/loan/loans-coins.png';

const CategoryDetailsStep = () => {
  const { updateStepStatus, setCurrentStep, closeWizard } = useWizardInfo();
  const { formData, setFormData, register } = useFormWithProvider<EmployeeLoansCategoryFormData>();
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleNextClick = () => {
    if (!formData.categoryName?.trim()) {
      return;
    }
    updateStepStatus(0, 'success');
    setCurrentStep(1);
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const isNextDisabled = !formData.categoryName?.trim();

  return (
    <>
      <ModalBody height="100%" padding="spacing.0">
        <Box width="100%" height="100%" position="relative" overflow="hidden">
          <Box position="absolute" bottom="0px" right="0px" zIndex={0}>
            <img src={LoanCoins} alt="" width={196} height={196} />
          </Box>
          <Box height="100%" overflowY="auto" position="relative" zIndex={1}>
            <Box
              display="flex"
              flexDirection="column"
              gap="spacing.8"
              paddingY="spacing.9"
              maxWidth="462px"
              margin="auto">
              <Box display="flex" flexDirection="column">
                <Heading size="large" weight="semibold" color="surface.text.gray.normal">
                  Basic Details
                </Heading>
                <Text size="medium" variant="body" weight="regular" color="surface.text.gray.muted">
                  Name your category and set processing rules
                </Text>
              </Box>
              <Box display="flex" flexDirection="column" position="relative">
                <TextInput
                  label="Loan or Advance Category"
                  placeholder="Category name"
                  necessityIndicator="required"
                  helpText="Choose a clear name that employees will recognise"
                  {...register('categoryName')}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                {showSuggestions && (
                  <Box
                    position="absolute"
                    top="100%"
                    left="0px"
                    right="0px"
                    zIndex={10}
                    marginTop="spacing.2"
                    padding="spacing.5"
                    backgroundColor="surface.background.gray.intense"
                    borderWidth="thin"
                    borderColor="surface.border.gray.subtle"
                    borderRadius="medium"
                    elevation="lowRaised">
                    <Text
                      size="small"
                      weight="medium"
                      color="surface.text.gray.muted"
                      marginBottom="spacing.4">
                      Suggested
                    </Text>
                    <ChipGroup
                      accessibilityLabel="Suggested category names"
                      selectionType="single"
                      size="small"
                      {...register('categoryName', 'chipgroup', 'single')}>
                      <Box display="flex" flexWrap="wrap" gap="spacing.3">
                        {SUGGESTED_CATEGORY_NAMES.map((name) => (
                          <Chip key={name} value={name}>
                            {name}
                          </Chip>
                        ))}
                      </Box>
                    </ChipGroup>
                  </Box>
                )}
              </Box>

              <Box display="flex" flexDirection="column" gap="spacing.3">
                <Box
                  display="flex"
                  flexDirection="column"
                  borderWidth="thinner"
                  overflow="hidden"
                  borderColor="surface.border.gray.muted"
                  borderRadius="medium">
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    paddingY="spacing.5"
                    paddingX="spacing.7"
                    backgroundColor="surface.background.gray.intense">
                    <Box display="flex" flexDirection="column" gap="spacing.2">
                      <Text
                        weight="medium"
                        size="small"
                        variant="body"
                        color="surface.text.gray.subtle">
                        Set as Critical Category{' '}
                        <Badge marginLeft="spacing.2" color="positive" size="small">
                          Suggested
                        </Badge>
                      </Text>
                      <Text
                        size="small"
                        weight="regular"
                        variant="body"
                        color="surface.text.gray.muted">
                        Flags requests as high-priority during the approval process.
                      </Text>
                    </Box>
                    <Switch
                      {...register('isCategoryCritical', 'switch')}
                      size="small"
                      accessibilityLabel="Mark as Critical Category"
                    />
                  </Box>
                  <Alert
                    description={
                      'Best suited for urgent needs like medical emergencies. May not be ideal for planned expenses.'
                    }
                    color="neutral"
                    isDismissible={false}
                    isFullWidth
                    emphasis="intense"
                  />
                </Box>
                <CardWrapper>
                  <Box display="flex" flexDirection="column" gap="spacing.3">
                    <Text
                      weight="medium"
                      size="small"
                      variant="body"
                      color="surface.text.gray.subtle">
                      Require Documentation from Employees
                    </Text>
                    <Text
                      size="small"
                      weight="regular"
                      variant="body"
                      color="surface.text.gray.muted">
                      Employees must upload proof documents
                    </Text>
                  </Box>
                  <Switch
                    {...register('isDocumentRequired', 'switch')}
                    size="small"
                    accessibilityLabel="Documentation Required from Employee"
                  />
                </CardWrapper>
              </Box>
            </Box>
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box width="100%" display="flex" justifyContent="center" alignItems="center">
          <Box display="flex" justifyContent="space-between" width="462px">
            <Button variant="tertiary" onClick={closeWizard}>
              Cancel
            </Button>
            <Button onClick={handleNextClick} isDisabled={isNextDisabled}>
              Continue
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </>
  );
};

export default CategoryDetailsStep;
